/* eslint-disable no-undef */
import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Avatar, Badge, Layout, Dropdown, Drawer, Divider, Select, Menu } from 'antd'
import { PoweroffOutlined } from '@ant-design/icons'
import useAlertLogList2 from '../../hook/useAlertLogList2'
import useGridList from '../../hook/useGridList'
import { SettingContext } from '../../store/SettingProvider'
import { AuthContext } from '../../store/AuthProvider'
import { DashboardContext } from '../../store/DashboardProvider.js'
import { DashboardContext as TimeLineDashboard } from '../../store/TimeLineDashboardProvider'
import logo_s from '../../images/logo_s.svg'
import dayjs from 'dayjs'
import { menu, getDateTimeRangeToEnd } from '../../functionTool/functionTool'

const { Header } = Layout

function searchKey(data = [], targetKey) {
    let results = [];
  
    for (let item of data) {
      if (item.key === targetKey) {
        results.push(item);
      }
  
      if (item.children) {
        results = results.concat(searchKey(item.children, targetKey));
      }
    }

    return results;
}

// 定義排序順序
const customOrder = ["場景一", "場景二", "場景三", "場景四", "場景五", "竹三辦"];

const AppHeader = () => {
    const { dark, customerSettings } = useContext(SettingContext)
    const { UnAuthUser, user } = useContext(AuthContext)
    const { setDate: timelineDashboardSetDate, setOffset: timelineDashboardSetOffset, setEndOfThatDay: timelineDashboardSetEndOfThatDay,  customTimeLineMode } = useContext(TimeLineDashboard)
    const { listID, setListID, setDate, setOffset, setListIDName } = useContext(DashboardContext)
    const navigate = useNavigate()
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const { alertLogList2 } = useAlertLogList2({ expired: 'false' })
    const { gridList, isFetching } = useGridList()

    //客戶logo url
    const logoUrl = customerSettings?.[0]?.profileInfo?.darkLogoURL || logo_s

    const isDemoAccountAndIsTimelineDashboard = (user?.userName === '展示' && process.env.REACT_APP_STAGE_NAME === '開發區') && location.pathname === '/timelinedashboard' ? true : false //是否展示帳號
    const isDemoAccountAndIsHome = user?.userName === '展示' && process.env.REACT_APP_STAGE_NAME === '開發區' && location.pathname === '/' ? true : false //展示帳號並且是首頁
    const isDemoAccount = user.userName === '展示' && process.env.REACT_APP_STAGE_NAME === '開發區' && customTimeLineMode //展示帳號
    
    const selectOptions = useMemo(() => {
        let data = gridList?.vpp_getTopologyVppGridList?.map((item) => ({
            value: item.topologyVPPGridID?.toUpperCase(),
            label: item.displayName,
        }))
        if (isDemoAccountAndIsTimelineDashboard) {
            data?.sort((a, b) => {
                // 取出 a 和 b 的 label 值
                const labelA = a.label;
                const labelB = b.label;
            
                // 找出 labelA 和 labelB 在 customOrder 中的索引
                const indexA = customOrder.findIndex(order => labelA.includes(order));
                const indexB = customOrder.findIndex(order => labelB.includes(order));
            
                // 按索引進行比較，索引小的排在前面
                return indexA - indexB;
            });
            data = data?.filter(i => i.value !== 'VPP0008')
        }
        if (isDemoAccountAndIsHome) data = data?.filter?.(i => i.value === 'VPP0008')
        return data
    }, [gridList, isDemoAccountAndIsHome, isDemoAccountAndIsTimelineDashboard])

    //判斷是否顯示嚴重程度：次要、提示，ABNORMAL_all-degree表示顯示所有嚴重程度
    const showAllDegree = useMemo(() => {
        return user.roleList?.[0].operations?.[0] === 'ABNORMAL_all-degree'
    }, [user])

    // 計算selectOptions最長的 label 寬度
    const maxLabelWidth = useMemo(() => {
        // 确保 selectOptions 是一个有效的数组
        if (!Array.isArray(selectOptions)) return 0; // 如果不是数组，返回 0
        const calculatedWidth = Math.max(...selectOptions.map(item => item.label.length)) * 14; // 假設每個字符的寬度約為 11px
        return Math.min(Math.max(calculatedWidth, 200), 500); // 限制最小寬度為 200，最大寬度不超過 500
    }, [selectOptions]) || 200

    // 更新 URL
    const updateURL = useCallback((vppID) => {
        const newParams = new URLSearchParams(location.search);
        newParams.set('vppID', vppID);
        navigate({ pathname: location.pathname, search: newParams.toString() }, { replace: true });
    }, [location.pathname, location.search, navigate]);

    // 選擇虛擬電網
    const handleSelect = useCallback((e, option) => {
        setListID(e)
        setListIDName(option.label)
        updateURL(e)
    },[setListID, setListIDName, updateURL])

    //登出
    const logoutHandler = () => {
        setListID('')
        UnAuthUser()
    }

    //錯誤訊息數量
    const alerNumber = alertLogList2?.vpp_getAlertLogList2?.filter?.(
        (item) => item.expired !== 'true' && (showAllDegree || item.alertLevel !== 'medium' && item.alertLevel !== 'low')
    )?.length

    const isDemo = process.env.REACT_APP_STAGE_NAME === '開發區' && customTimeLineMode

    const appHeaderMenu = useMemo(() => menu({
        alert: alerNumber,
        customTimeLineMode: isDemo,
        menuList: user?.menuList || [],
    }),[alerNumber, isDemo, user?.menuList])

    // 展示帳號需過濾掉指定頁面
    const finalAppHeaderMenu = useMemo(() => {
        if (!isDemoAccount) return appHeaderMenu
        const disallowedKeys = new Set([
            '/analysis',            // 分析報告
            '/greenenergyinfo',     // 綠電資訊報表（整組）
            '/emsreport',           // 維運與成效報告
            '/device',              // 能源設備總覽
            '/contractconfig',      // 契約容量管理
        ])

        const prune = (items = []) => {
            return items.reduce((acc, item) => {
                if (disallowedKeys.has(item.key)) return acc
                let newItem = { ...item }
                if (item.children && item.children.length) {
                    const children = prune(item.children)
                    if (children.length) {
                        newItem.children = children
                    } else {
                        delete newItem.children
                    }
                }
                acc.push(newItem)
                return acc
            }, [])
        }

        return prune(appHeaderMenu)
    }, [isDemoAccount, appHeaderMenu])

    const items = [
        {
            key: 'app-logout',
            label: <span onClick={logoutHandler}>登出</span>,
            icon: (
                <span onClick={logoutHandler}>
                    <PoweroffOutlined />
                </span>
            ),
            name: '登出',
            path: '',
        },
    ]
    const showDrawer = () => {
        setOpen(true)
    }

    const onClose = () => {
        setOpen(false)
    }

    const updateOffset = useCallback(
        (newDate) => {
            const { startDatetime, endDatetime } = getDateTimeRangeToEnd(
                'day',
                newDate
            )
            setOffset({
                refAreaLeft: '',
                refAreaRight: '',
                left: Date.parse(startDatetime),
                right: Date.parse(endDatetime),
            })
        },
        [setOffset]
    )

    const currentSelectedMenuKey = useMemo(() => {
        if (location.pathname === '/') return '/';
    
        const pathSnippets = location.pathname.split('/').filter((i) => i);
    
        let menuKey = '';
        for (let i = pathSnippets.length; i > 0; i--) {
          const url = `/${pathSnippets.slice(0, i).join('/')}`;
          // const matchMenu = MenuList.find((f) => f.key === url);
          const matchMenu = searchKey(finalAppHeaderMenu, url);
          if (matchMenu.length) {
            menuKey = matchMenu[0].key;
            break;
          }
        }
    
        return menuKey;
    }, [location, finalAppHeaderMenu]);

    //展示帳號且是首頁set用
    useEffect(() => {
        if (listID) {
            if (isDemoAccountAndIsHome) {
                let date = dayjs();
                if (listID === 'VPP0008') date = dayjs('2025-09-30')
                if (listID === 'VPP0004') date = dayjs('2024-06-06')
                if (listID === 'VPP0005') date = dayjs('2024-07-04')
                if (listID === 'VPP0006') date = dayjs('2024-09-18')
                if (listID === 'VPP0009') date = dayjs('2024-09-13')
                if (listID === 'VPP0007') date = dayjs('2024-09-20')
                setDate(date)
                updateOffset(date)
            }
        }
    }, [isDemoAccountAndIsHome, listID])

    //展示帳號且是timeline dashboard set用
    useEffect(() => {
        if (listID) {
            if (isDemoAccountAndIsTimelineDashboard) {
                let date = dayjs();
                if (listID === 'VPP0008') date = dayjs('2024-08-23')
                if (listID === 'VPP0004') date = dayjs('2024-06-06')
                if (listID === 'VPP0005') date = dayjs('2024-07-04')
                if (listID === 'VPP0006') date = dayjs('2024-09-18')
                if (listID === 'VPP0009') date = dayjs('2024-09-13')
                if (listID === 'VPP0007') date = dayjs('2024-09-20')
                timelineDashboardSetDate(date)
                timelineDashboardSetOffset({
                    refAreaLeft: '',
                    refAreaRight: '',
                    left: 0,
                    right: 1440,
                })
                if (!dayjs().isSame(date, 'day')) {
                    timelineDashboardSetEndOfThatDay(true)
                }
            }
        }
    }, [listID, isDemoAccountAndIsTimelineDashboard])

    // EMS 調度展示帳號且是首頁set用
    const isEmsDashboardDemoAccount = user?.userName === 'EMS 調度展示帳號'
    const isEmsDemoAccountAndIsHome = isEmsDashboardDemoAccount && location.pathname === '/'
    
    useEffect(() => {
        if (isEmsDemoAccountAndIsHome) {
            const date = dayjs('2025-09-30')
            setDate(date)
            const { startDatetime, endDatetime } = getDateTimeRangeToEnd('day', date)
            setOffset({
                refAreaLeft: '',
                refAreaRight: '',
                left: Date.parse(startDatetime),
                right: Date.parse(endDatetime),
            })
        }
    }, [isEmsDemoAccountAndIsHome])
 
    useEffect(() => {
        if (!selectOptions?.length) {
            return; // 等待選項載入
        }

        const params = new URLSearchParams(location.search);
        const vppIDFromURL = params.get('vppID')?.toUpperCase();
        
        // 1. 如果 URL 中的 vppID 有效，則優先使用
        if (vppIDFromURL && selectOptions.some(opt => opt.value === vppIDFromURL)) {
            if (listID !== vppIDFromURL) {
                setListID(vppIDFromURL);
            }
            return;
        }

        // 2. 如果沒有有效的 URL 參數，請檢查目前的 listID 是否有效
        const currentListIdIsValid = selectOptions.some(opt => opt.value === listID);
        if (currentListIdIsValid) {
            // 當前 listID 有效，更新 URL 以匹配 listID
            updateURL(listID);
            return;
        }

        // 3. 如果目前的 listID 無效，則退回至第一個選項
        const defaultVppID = selectOptions[0].value;
        setListID(defaultVppID);
        updateURL(defaultVppID);
    }, [selectOptions, location.search, listID, setListID, updateURL]);

    // 確保 selectOptions 或 listID 變動時，listIDName 會正確同步
    useEffect(() => {
        if (!selectOptions?.length || !listID) return;
        const found = selectOptions.find(opt => opt.value === listID);
        if (found) setListIDName(found.label);
    }, [selectOptions, listID]);

    return (
        <Header className="app-header">
            <div className="app-header-menu">
                <div className="app-header-logo">
                    <Link to="/">
                        <img src={logoUrl} alt="微電能源-能源管理系統" />
                    </Link>
                </div>                
                <div className="app-header-note">
                    歡迎來到能源管理平台 {user.userName}
                </div>
                <div className="app-header-right-menu-container">
                    {isDemoAccountAndIsHome ? null : <div className="app-header-topology">
                        <Select
                            popupClassName={dark ? 'dark' : ''}
                            placeholder={
                                gridList?.vpp_getTopologyVppGridList?.[0]?.displayName
                            }
                            value={listID}
                            options={selectOptions}
                            onSelect={handleSelect}
                            loading={isFetching}
                            style={{minWidth: maxLabelWidth}}
                        />
                    </div>}
                    <ul className="app-header-right-menu">
                        <li>
                            <div className="user">
                                <Dropdown
                                    menu={{ items }}
                                    trigger={['click']}
                                    overlayClassName={dark ? 'dark' : ''}
                                >
                                    <div>
                                        <Badge size="small" count={0}>
                                            <Avatar
                                                size="small"
                                                style={{
                                                    color: '#f56a00',
                                                    backgroundColor: '#fde3cf',
                                                }}
                                            >
                                                {user?.userName
                                                    ?.substr(0, 1)
                                                    .toUpperCase()}
                                            </Avatar>
                                        </Badge>
                                        {/* <span className="name">
                                            {user.userName}
                                        </span> */}
                                    </div>
                                </Dropdown>
                            </div>
                        </li>
                    </ul>                          
                    <div
                        className="app-header-mobile-menu"
                        onClick={() => {
                            showDrawer()
                        }}
                    >
                        <em />
                        <em />
                    </div>
                </div>
            </div>
            <Drawer
                title=""
                placement="right"
                onClose={onClose}
                open={open}
                width={300}
                className={dark ? 'dark' : ''}
            >
                {isDemoAccountAndIsHome ? null :<div style={{marginBottom: 20}}>
                    <Select
                        popupClassName={dark ? 'dark' : ''}
                        placeholder={
                            gridList?.vpp_getTopologyVppGridList?.[0]?.displayName
                        }
                        value={listID}
                        options={selectOptions}
                        onSelect={handleSelect}
                        loading={isFetching}
                        style={{width: '100%'}}
                    />
                </div>}
                <Menu
                    theme={dark ? 'dark' : 'light'}
                    mode="inline"
                    className="app-aside-menu"
                    inlineIndent={16}
                    selectedKeys={[currentSelectedMenuKey]}
                    items={finalAppHeaderMenu}
                />
                <Divider />
                {items.map((item) => (
                    <p key={item.key}>
                        <span style={{marginRight: 8}}>{item.icon}</span>
                        <span
                            onClick={() => {
                                if (item.name === '登出') {
                                    logoutHandler()
                                } else {
                                    navigate(item.path)
                                }
                                onClose()
                            }}
                        >
                            {item.name}
                        </span>
                    </p>
                ))}
            </Drawer>
        </Header>
    )
}

export default AppHeader
